import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import type {ColumnDef, Row} from "@tanstack/react-table";
import ProfilePreviewDialog from "crqlar-react-common/dist/components/profilePreview/ProfilePreviewDialog";
import SurveyInviteButton from "../surveys/share/SurveyInviteButton";
import type {
    DailyRestaurantV3RequestDTO,
    DailyRestaurantV3ReservationDTO,
    DailyRestaurantV3RowDTO,
} from "crqlar-react-common/dist/models/aggregation/dailyAggregation.model";
import type {ResourceDisplayReference} from "crqlar-react-common/dist/models/aggregation/aggregation.model";
import {
    ReservationListMedicalFoodPreferenceType,
} from "crqlar-react-common/dist/models/aggregation/reservationListAggregation.model";
import {
    DataFilterOperator,
    type Filter,
    type FilterRule,
} from "crqlar-react-common/dist/models/audience/filter.model";
import type {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import type {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {DailyListAggregationService} from "crqlar-react-common/dist/services/aggregation/dailyListAggregation.service";
import {ProfileService} from "crqlar-react-common/dist/services/profile/profile.service";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {formatDateHM} from "crqlar-react-common/dist/utils/date.util";
import {enqueueSnackbar} from "notistack";
import {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQueryClient} from "react-query";
import {
    RightSidebarDivider,
    RightSidebarPanelButton,
    RightSidebarRail,
    RightSidebarRailHeader,
    RightSidebarRailHeaderCenter,
    RightSidebarRailLayout,
} from "../layout/rightSidebar/RightSidebarRail";
import {
    RadioFilterOption,
    RadioFilterPanel,
} from "../layout/rightSidebar/RadioFilterPanel";
import {
    StayStatusFilterValue,
    StayStatusRailFilter,
} from "../layout/rightSidebar/StayStatusRailFilter";
import SingleDatePicker from "../common/newDatePicker/SingleDatePicker";
import {useDebouncedSingleDate} from "../common/newDatePicker/datePickerShared";
import {
    ServerTable,
    ServerTableFrame,
    ServerTableProvider,
    ServerTableRailColumns,
    ServerTableRailExport,
    ServerTableRailFullscreen,
    ServerTableRailPrint,
    ServerTableRailSearch,
    ServerTableRailSort,
    serverTableExportLimit,
    type ServerTableExportQuery,
    useServerTable,
} from "../common/serverTable";
import {formatProfileListName} from "crqlar-react-common/dist/utils/profile.util";
import {AllergiesElement} from "../common/uiElements/AllergiesElement";
import {BirthdayElement} from "../common/uiElements/BirthdayElement";
import {CountryElement} from "../common/uiElements/CountryElement";
import {DateElement} from "../common/uiElements/DateElement";
import {DietsElement} from "../common/uiElements/DietsElement";
import {GenderElement} from "../common/uiElements/GenderElement";
import {LanguagesElement} from "../common/uiElements/LanguagesElement";
import {MedicalElement} from "../common/uiElements/MedicalElement";
import {NationalityElement} from "../common/uiElements/NationalityElement";
import {NotesElement} from "../common/uiElements/NotesElement";
import {noteTexts} from "crqlar-react-common/dist/utils/noteTypeGroup.util";
import {RecommendationsElement} from "../common/uiElements/RecommendationsElement";
import {ReservationStatusElement} from "../common/uiElements/ReservationStatusElement";
import {
    ReservationsElement,
    type ReservationElementItem,
} from "../common/uiElements/ReservationsElement";
import {
    AccommodationReferences,
    RoomStatusElement,
    RestaurantElement,
    RestaurantTableElement,
} from "../common/uiElements/ResourceElement";
import {SimpleChipElement} from "../common/uiElements/SimpleChipElement";
import {StayRangeElement} from "../common/uiElements/StayRangeElement";
import {StayStatusElement} from "../common/uiElements/StayStatusElement";
import {getLocalizedValue} from "../common/uiElements/utils";
import MultipleRestaurantResourcePicker from "../reservations/rail/MultipleRestaurantResourcePicker";
import {useReservationResourceSelection} from "../reservations/rail/useReservationResourceSelection";
import RestaurantEventsInfoButton from "../reservations/restaurantActionCenter/RestaurantEventsInfoButton";
import ReservationDialog from "../reservations/reservationDialog/ReservationDialog";
import {createReservationUpdatedColumn} from "../reservations/lists/reservationUpdatedColumn";
import {useCompactDashboardLayout} from "../../utils/responsive.util";
import {ageFromBirthday} from "./dailyListAge";
import {applyDailyListColumnDefaults} from "./dailyListDefaults";
import {RestaurantDailyListFilterButton} from "./filters/DailyListFilterButtons";
import RestaurantDailyEvents from "./RestaurantDailyEvents";

const getSubRows = (row: DailyRestaurantV3RowDTO) => row.children;

const localizedInterestValues = (row: DailyRestaurantV3RowDTO, locale: string) => (
    (row.interests ?? [])
        .map(interest => getLocalizedValue(interest.localizations, locale) ?? interest.value)
        .filter((value): value is string => Boolean(value))
);

const preferenceValues = (
    row: DailyRestaurantV3RowDTO,
    preferenceType: ReservationListMedicalFoodPreferenceType,
) => row.medicalFoodPreferences
    ?.filter(preference => preference.preferenceType === preferenceType)
    .map(preference => preference.preferenceValue) ?? [];

const reservationResources = (
    row: DailyRestaurantV3RowDTO,
    select: (reservation: DailyRestaurantV3ReservationDTO) => ResourceDisplayReference[] | undefined,
) => Array.from(new Map(
    row.restaurantReservations
        .flatMap(reservation => select(reservation) ?? [])
        .map(resource => [resource.id, resource]),
).values());

const restaurantReservationLabel = (reservation: DailyRestaurantV3ReservationDTO) => {
    const restaurantNames = reservation.restaurants?.map(resource => resource.name).filter(Boolean).join(", ");
    const tableNames = reservation.tables?.map(resource => resource.name).filter(Boolean).join(", ");
    return [tableNames, restaurantNames].filter(Boolean).join(" · ") || undefined;
};

const restaurantReservationItems = (row: DailyRestaurantV3RowDTO): ReservationElementItem[] => (
    row.restaurantReservations.map(reservation => ({
        reservationId: reservation.reservationId,
        status: reservation.status,
        start: reservation.start,
        end: reservation.end,
        restaurants: reservation.restaurants,
        tables: reservation.tables,
        guestCount: reservation.guestCount,
    }))
);

type DailyListViewMode = "GROUPED" | "INDIVIDUAL";

export default function RestaurantDailyList({sectorId}: { sectorId: string }) {
    const {t, i18n} = useTranslation();
    const queryClient = useQueryClient();
    const isCompactLayout = useCompactDashboardLayout();
    const resourceSelection = useReservationResourceSelection({sectorId, viewKey: "daily"});
    const {date: selectedDate, queryDate, onDateChange} = useDebouncedSingleDate(sectorId);
    const [eventsOpen, setEventsOpen] = useState(false);
    const [eventCount, setEventCount] = useState<number>();
    const [viewMode, setViewMode] = useState<DailyListViewMode>("GROUPED");
    const [stayStatus, setStayStatus] = useState<StayStatusFilterValue>("ALL");
    const [filterRules, setFilterRules] = useState<FilterRule[]>([]);
    const [currentProfile, setCurrentProfile] = useState<Profile>();
    const [currentReservationIds, setCurrentReservationIds] = useState<string>();
    const [selectedReservation, setSelectedReservation] = useState<Reservation>();
    const [selectedReservationProfile, setSelectedReservationProfile] = useState<Profile>();
    const grouped = viewMode === "GROUPED";
    const locale = i18n.resolvedLanguage || i18n.language;
    const filters = useMemo<Filter | undefined>(() => filterRules.length ? {
        filterGroups: [{filterRules, operator: DataFilterOperator.AND}],
    } : undefined, [filterRules]);
    const selectedResourceIds = resourceSelection.resources.length === 0 || resourceSelection.allSelected
        ? undefined
        : resourceSelection.selectedIds;
    const selectedResourceIdsKey = selectedResourceIds?.join(",") ?? "all";
    const noRestaurantsSelected = resourceSelection.resources.length > 0
        && resourceSelection.selectedIds.length === 0;

    const getRowId = useCallback((
        row: DailyRestaurantV3RowDTO,
        _index: number,
        parent?: Row<DailyRestaurantV3RowDTO>,
    ) => parent ? `${parent.id}/${row.rowId}` : row.rowId, []);

    const viewModeOptions = useMemo<readonly RadioFilterOption<DailyListViewMode>[]>(() => [
        {value: "GROUPED", label: t("Grouped")},
        {value: "INDIVIDUAL", label: t("Individual guests")},
    ], [t]);

    const openProfileDialog = useCallback(async (row: DailyRestaurantV3RowDTO) => {
        if (!row.profileId) return;

        setCurrentReservationIds(row.restaurantReservations.map(reservation => reservation.reservationId).filter(Boolean).join(", "));
        try {
            setCurrentProfile(await ProfileService.getProfileById(row.profileId));
        } catch (error) {
            console.error("Could not open profile", error);
        }
    }, []);

    const openReservationDialog = useCallback((reservation: Reservation, profile?: Profile) => {
        setSelectedReservationProfile(profile);
        setSelectedReservation(reservation);
    }, []);

    const closeReservationDialog = useCallback(() => {
        setSelectedReservation(undefined);
        setSelectedReservationProfile(undefined);
    }, []);

    const openReservationById = useCallback(async (reservationId: string | undefined) => {
        if (!reservationId) return;

        try {
            const fullReservation = await ReservationService.getReservationById(reservationId);
            if (!fullReservation) throw new Error("Reservation not found");
            openReservationDialog(fullReservation);
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t("Failed to open reservation"), {variant: "error"});
        }
    }, [openReservationDialog, t]);

    const handleOpenReservation = useCallback(
        (reservation: ReservationElementItem) => openReservationById(reservation.reservationId),
        [openReservationById],
    );

    const columns = useMemo<ColumnDef<DailyRestaurantV3RowDTO>[]>(() => applyDailyListColumnDefaults("restaurant", [
        {
            id: "name",
            header: t("Name"),
            accessorFn: row => formatProfileListName(row),
            cell: ({getValue, row}) => (
                <Box sx={{fontWeight: row.getCanExpand() ? 600 : undefined}}>
                    {getValue<string | undefined>() ?? null}
                </Box>
            ),
            size: 220,
            meta: {
                isServerSortable: true,
                serverSortField: "lastName",
                printable: true,
                printDefault: true,
            },
        },
        {
            id: "age",
            header: t("Age"),
            accessorFn: row => ageFromBirthday(row.birthday),
            enableSorting: false,
            cell: ({getValue}) => <Box>{getValue<number | undefined>() ?? null}</Box>,
            size: 90,
            meta: {
                printable: true,
                printDefault: true,
                printCell: row => row.getValue<number | undefined>("age") ?? "",
            },
        },
        {
            id: "restaurantReservations",
            header: t("Reservations"),
            accessorFn: row => row.restaurantReservations.map(reservation => {
                const time = reservation.start ? formatDateHM(reservation.start) : undefined;
                const guestCount = reservation.guestCount === undefined
                    ? undefined
                    : t("Guest count short", {count: reservation.guestCount});
                return [time, restaurantReservationLabel(reservation), guestCount].filter(Boolean).join(" ");
            }).join("; "),
            cell: ({row}) => row.depth === 0 ? (
                <ReservationsElement
                    reservations={restaurantReservationItems(row.original)}
                    now={new Date()}
                    dateContext={queryDate}
                    onSelect={handleOpenReservation}
                />
            ) : null,
            size: 380,
            meta: {
                isServerSortable: true,
                serverSortField: "restaurantReservations",
                grow: true,
                printable: true,
                printDefault: true,
                // A group's guest rows share its booking: it prints once, on the group row, as on screen.
                printCell: row => row.depth === 0 ? row.getValue<string>("restaurantReservations") : "",
                printOriginalCell: row => row.depth === 0 ? (
                    <ReservationsElement
                        reservations={restaurantReservationItems(row.original)}
                        now={new Date()}
                        asPrint
                        printVariant="original"
                        dateContext={queryDate}
                    />
                ) : null,
            },
        },
        {
            id: "rooms",
            header: t("Room"),
            accessorFn: row => row.accommodationResources?.map(resource => resource.name).filter(Boolean) ?? [],
            cell: ({row}) => <AccommodationReferences resources={row.original.accommodationResources}/>,
            size: 110,
            meta: {
                isServerSortable: true,
                serverSortField: "accommodationResources",
                printable: true,
                printDefault: true,
                printCell: row => row.original.accommodationResources
                    ?.map(resource => resource.name).filter(Boolean).join(", ") ?? null,
            },
        },
        {
            // Board code when the property mapped its rates (Casablanca), else the service name.
            id: "rateCode",
            header: t("Rate code"),
            accessorFn: row => row.rateCode,
            cell: ({getValue}) => getValue<string | undefined>() ?? null,
            size: 120,
            meta: {
                isServerSortable: true,
                printable: true,
                printCell: row => row.original.rateCode ?? null,
            },
        },
        {
            id: "roomStatus",
            header: t("Room status"),
            accessorFn: row => row.accommodationResources?.map(resource => resource.name).filter(Boolean) ?? [],
            cell: ({row}) => (
                <RoomStatusElement
                    resources={row.original.accommodationResources}
                    stayStatus={row.original.stayStatus}
                    onClick={row.original.reservationId
                        ? () => void openReservationById(row.original.reservationId)
                        : undefined}
                />
            ),
            size: 180,
            meta: {
                printable: true,
                printCell: row => (
                    <RoomStatusElement
                        resources={row.original.accommodationResources}
                        stayStatus={row.original.stayStatus}
                        asPrint
                    />
                ),
            },
        },
        {
            id: "countryCode",
            header: t("Country"),
            accessorFn: row => row.countryCode,
            cell: ({row}) => <CountryElement countryCode={row.original.countryCode}/>,
            size: 130,
            meta: {
                printable: true,
                printDefault: true,
                printCell: row => <CountryElement countryCode={row.original.countryCode} asPrint/>,
            },
        },
        {
            id: "nationality",
            header: t("Nationality"),
            accessorFn: row => row.nationality,
            cell: ({row}) => <NationalityElement nationality={row.original.nationality}/>,
            size: 130,
            meta: {
                printable: true,
                printDefault: true,
                printCell: row => <NationalityElement nationality={row.original.nationality} asPrint/>,
            },
        },
        {
            id: "languageCodes",
            header: t("Languages"),
            accessorFn: row => row.languageCodes ?? [],
            cell: ({row}) => <LanguagesElement languageCodes={row.original.languageCodes}/>,
            size: 150,
            meta: {
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => <LanguagesElement languageCodes={row.original.languageCodes} asPrint/>,
            },
        },
        {
            id: "stayRange",
            header: t("Stay range"),
            accessorFn: row => row.stayStart,
            cell: ({row}) => (
                <StayRangeElement
                    start={row.original.stayStart}
                    end={row.original.stayEnd}
                    locale={locale}
                />
            ),
            size: 180,
            meta: {
                isServerSortable: true,
                serverSortField: "stayStart",
                printable: true,
                printDefault: true,
                printCell: row => (
                    <StayRangeElement
                        start={row.original.stayStart}
                        end={row.original.stayEnd}
                        locale={locale}
                    />
                ),
            },
        },
        {
            id: "allergiesAndDiets",
            header: t("Allergies & diets"),
            accessorFn: row => [
                ...preferenceValues(row, ReservationListMedicalFoodPreferenceType.CRQLAR_FOOD_ALLERGY),
                ...preferenceValues(row, ReservationListMedicalFoodPreferenceType.CRQLAR_FOOD_TYPE),
            ],
            cell: ({row}) => {
                const allergies = preferenceValues(
                    row.original,
                    ReservationListMedicalFoodPreferenceType.CRQLAR_FOOD_ALLERGY,
                );
                const diets = preferenceValues(
                    row.original,
                    ReservationListMedicalFoodPreferenceType.CRQLAR_FOOD_TYPE,
                );
                if (!allergies.length && !diets.length) return null;

                return (
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                        <AllergiesElement allergies={allergies}/>
                        <DietsElement diets={diets}/>
                    </Box>
                );
            },
            size: 300,
            meta: {
                printable: true,
                printDefault: true,
                printCell: row => (
                    <SimpleChipElement
                        values={[
                            ...preferenceValues(
                                row.original,
                                ReservationListMedicalFoodPreferenceType.CRQLAR_FOOD_ALLERGY,
                            ),
                            ...preferenceValues(
                                row.original,
                                ReservationListMedicalFoodPreferenceType.CRQLAR_FOOD_TYPE,
                            ),
                        ]}
                        asPrint
                    />
                ),
            },
        },
        {
            id: "medical",
            header: t("Medical"),
            accessorFn: row => preferenceValues(
                row,
                ReservationListMedicalFoodPreferenceType.CRQLAR_MEDICAL,
            ),
            cell: ({row}) => (
                <MedicalElement
                    medical={preferenceValues(
                        row.original,
                        ReservationListMedicalFoodPreferenceType.CRQLAR_MEDICAL,
                    )}
                />
            ),
            size: 220,
            meta: {
                printable: true,
                printDefault: true,
                printCell: row => (
                    <SimpleChipElement
                        values={preferenceValues(
                            row.original,
                            ReservationListMedicalFoodPreferenceType.CRQLAR_MEDICAL,
                        )}
                        asPrint
                    />
                ),
            },
        },
        {
            id: "interests",
            header: t("Interests"),
            accessorFn: row => localizedInterestValues(row, locale),
            cell: ({row}) => (
                <SimpleChipElement values={localizedInterestValues(row.original, locale)}/>
            ),
            size: 250,
            meta: {
                printable: true,
                printDefault: true,
                printCell: row => (
                    <SimpleChipElement values={localizedInterestValues(row.original, locale)} asPrint/>
                ),
            },
        },
        {
            id: "recommendations",
            header: t("Recommendations"),
            accessorFn: row => [
                ...(row.warningRecommendation ?? []),
                ...(row.informationRecommendation ?? []),
                ...(row.successRecommendation ?? []),
            ],
            cell: ({row}) => (
                <RecommendationsElement
                    warningMessages={row.original.warningRecommendation}
                    informationMessages={row.original.informationRecommendation}
                    successMessages={row.original.successRecommendation}
                    locale={locale}
                />
            ),
            size: 360,
            meta: {
                printable: true,
                printDefault: true,
                printCell: row => (
                    <RecommendationsElement
                        warningMessages={row.original.warningRecommendation}
                        informationMessages={row.original.informationRecommendation}
                        successMessages={row.original.successRecommendation}
                        locale={locale}
                        printMode
                    />
                ),
            },
        },
        {
            id: "restaurants",
            header: t("Restaurant"),
            accessorFn: row => reservationResources(row, reservation => reservation.restaurants)
                .map(resource => resource.name).filter(Boolean),
            cell: ({row}) => {
                const resources = reservationResources(row.original, reservation => reservation.restaurants);
                if (!resources.length) return null;

                return (
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                        {resources.map(resource => (
                            <RestaurantElement
                                key={resource.id}
                                name={resource.name ?? ""}
                                color={resource.tagColor}
                            />
                        ))}
                    </Box>
                );
            },
            size: 220,
            meta: {
                isServerSortable: true,
                printable: true,
                printCell: row => reservationResources(row.original, reservation => reservation.restaurants)
                    .map(resource => resource.name).filter(Boolean).join(", "),
            },
        },
        {
            id: "tables",
            header: t("Tables"),
            accessorFn: row => reservationResources(row, reservation => reservation.tables)
                .map(resource => resource.name).filter(Boolean),
            cell: ({row}) => {
                const resources = reservationResources(row.original, reservation => reservation.tables);
                if (!resources.length) return null;

                return (
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                        {resources.map(resource => (
                            <RestaurantTableElement
                                key={resource.id}
                                name={resource.name ?? ""}
                                color={resource.tagColor}
                                parentColor={resource.parent?.tagColor}
                            />
                        ))}
                    </Box>
                );
            },
            size: 220,
            meta: {
                isServerSortable: true,
                printable: true,
                printCell: row => reservationResources(
                    row.original,
                    reservation => reservation.tables,
                ).map(resource => resource.name).filter(Boolean).join(", "),
            },
        },
        {
            id: "resources",
            header: t("Resources"),
            accessorFn: row => reservationResources(row, reservation => [
                ...(reservation.restaurants ?? []),
                ...(reservation.tables ?? []),
            ]).map(resource => resource.name).filter(Boolean),
            cell: ({row}) => {
                const names = reservationResources(row.original, reservation => [
                    ...(reservation.restaurants ?? []),
                    ...(reservation.tables ?? []),
                ]).map(resource => resource.name).filter((name): name is string => Boolean(name));
                return <SimpleChipElement values={names}/>;
            },
            size: 240,
            meta: {
                isServerSortable: true,
                printable: true,
                printCell: row => reservationResources(row.original, reservation => [
                    ...(reservation.restaurants ?? []),
                    ...(reservation.tables ?? []),
                ]).map(resource => resource.name).filter(Boolean).join(", "),
            },
        },
        {
            id: "reservationStatus",
            header: t("Reservation status"),
            accessorFn: row => row.restaurantReservations[0]?.status,
            cell: ({row}) => {
                const reservation = row.original.restaurantReservations[0];
                return (
                    <ReservationStatusElement
                        status={reservation?.status}
                        start={reservation?.start}
                        end={reservation?.end}
                    />
                );
            },
            size: 180,
            meta: {
                isServerSortable: true,
                printable: true,
                printCell: row => (
                    <ReservationStatusElement
                        status={row.original.restaurantReservations[0]?.status}
                        asPrint
                    />
                ),
            },
        },
        {
            id: "guestCount",
            header: t("Guest count abbreviation"),
            accessorFn: row => row.guestCount,
            cell: ({getValue}) => <Box>{getValue<number | undefined>() ?? null}</Box>,
            size: 110,
            meta: {isServerSortable: true, printable: true},
        },
        {
            id: "birthday",
            header: t("Birthday"),
            accessorFn: row => row.birthday,
            cell: ({row}) => (
                <BirthdayElement
                    birthday={row.original.birthday}
                    selectedDate={queryDate}
                    name={formatProfileListName(row.original)}
                />
            ),
            size: 170,
            meta: {
                isServerSortable: true,
                serverSortField: "birthday",
                printable: true,
                printCell: row => (
                    <BirthdayElement
                        birthday={row.original.birthday}
                        selectedDate={queryDate}
                        name={formatProfileListName(row.original)}
                    />
                ),
            },
        },
        {
            id: "stayStatus",
            header: t("Stay status"),
            accessorFn: row => row.stayStatus,
            cell: ({row}) => (
                <StayStatusElement
                    stayStatus={row.original.stayStatus}
                    startDate={row.original.stayStart}
                    endDate={row.original.stayEnd}
                />
            ),
            size: 170,
            meta: {
                isServerSortable: true,
                printable: true,
                printCell: row => <StayStatusElement stayStatus={row.original.stayStatus} asPrint/>,
            },
        },
        {
            id: "start",
            header: t("Start"),
            accessorFn: row => row.start,
            cell: ({row}) => <DateElement date={row.original.start}/>,
            size: 160,
            meta: {isServerSortable: true, printable: true},
        },
        {
            id: "end",
            header: t("End"),
            accessorFn: row => row.end,
            cell: ({row}) => <DateElement date={row.original.end}/>,
            size: 160,
            meta: {isServerSortable: true, printable: true},
        },
        {
            id: "gender",
            header: t("Gender"),
            accessorFn: row => row.gender,
            cell: ({row}) => <GenderElement gender={row.original.gender}/>,
            size: 140,
            meta: {
                printable: true,
                printCell: row => <GenderElement gender={row.original.gender}/>,
            },
        },
        {
            id: "voiceNotes",
            header: t("Voice Notes"),
            accessorFn: row => noteTexts(row.voiceNotes),
            cell: ({row}) => <NotesElement notes={row.original.voiceNotes}/>,
            size: 280,
            meta: {
                printable: true,
                printCell: row => <NotesElement notes={row.original.voiceNotes} asPrint/>,
            },
        },
        {
            id: "email",
            header: t("Email"),
            accessorFn: row => row.primaryEmail,
            cell: ({getValue}) => <Box>{getValue<string | undefined>() ?? null}</Box>,
            size: 260,
            meta: {isServerSortable: true, serverSortField: "primaryEmail", printable: true},
        },
        {
            id: "firstName",
            header: t("First name"),
            accessorFn: row => row.firstName,
            cell: ({getValue}) => <Box>{getValue<string | undefined>() ?? null}</Box>,
            size: 160,
            meta: {isServerSortable: true, printable: true},
        },
        {
            id: "lastName",
            header: t("Last name"),
            accessorFn: row => row.lastName,
            cell: ({getValue}) => <Box>{getValue<string | undefined>() ?? null}</Box>,
            size: 160,
            meta: {isServerSortable: true, printable: true},
        },
        {
            id: "academicTitle",
            header: t("Academic title"),
            accessorFn: row => row.academicTitle,
            cell: ({getValue}) => <Box>{getValue<string | undefined>() ?? null}</Box>,
            size: 150,
            meta: {printable: true},
        },
        {
            id: "namePrefix",
            header: t("Name prefix"),
            accessorFn: row => row.namePrefix,
            cell: ({getValue}) => <Box>{getValue<string | undefined>() ?? null}</Box>,
            size: 140,
            meta: {printable: true},
        },
        {
            id: "id",
            header: t("ID"),
            accessorFn: row => row.profileId,
            cell: ({getValue}) => <Box>{getValue<string | undefined>() ?? null}</Box>,
            size: 260,
            meta: {isServerSortable: true, serverSortField: "profileId"},
        },
        {
            id: "reservationId",
            header: t("Reservation ID"),
            accessorFn: row => row.reservationId,
            cell: ({getValue}) => <Box>{getValue<string | undefined>() ?? null}</Box>,
            size: 260,
            meta: {isServerSortable: true},
        },
        {
            id: "groupId",
            header: t("Group ID"),
            accessorFn: row => row.groupId,
            cell: ({getValue}) => <Box>{getValue<string | undefined>() ?? null}</Box>,
            size: 260,
            meta: {isServerSortable: true},
        },
        createReservationUpdatedColumn<DailyRestaurantV3RowDTO>(t("Reservation updated")),
    ]), [handleOpenReservation, locale, openReservationById, queryDate, t]);

    const controller = useServerTable<DailyRestaurantV3RowDTO>({
        tableId: "restaurant-daily-list-v3",
        columns,
        getRowId,
        getSubRows: grouped ? getSubRows : undefined,
        // Print covers the whole filtered day, not only the loaded page (CV3-1876).
        allRows: {},
        onRowClick: openProfileDialog,
        initialSorting: [{id: "restaurantReservations", desc: false}],
        queryDependencies: [sectorId, queryDate.getTime(), viewMode, stayStatus, selectedResourceIdsKey, filters],
        queryFn: async ({pagination, searchQuery, sorting}) => {
            if (noRestaurantsSelected) {
                return {content: [], totalElements: 0};
            }

            const page = await DailyListAggregationService.listRestaurant({
                sectorId,
                date: queryDate,
                grouped,
                resourceIds: selectedResourceIds,
                stayStatuses: stayStatus === "ALL" ? undefined : [stayStatus],
                filters,
                query: searchQuery || undefined,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined,
                },
            });

            return {
                content: page.content,
                totalElements: page.totalElements,
            };
        },
    });

    // The export's request: the list's own, without paging; the API writes one row per guest (CV3-1876).
    const exportRequest = useCallback((
        {searchQuery, sorting}: ServerTableExportQuery,
    ): DailyRestaurantV3RequestDTO | undefined => noRestaurantsSelected ? undefined : {
        sectorId,
        date: queryDate,
        grouped,
        resourceIds: selectedResourceIds,
        stayStatuses: stayStatus === "ALL" ? undefined : [stayStatus],
        filters,
        query: searchQuery || undefined,
        limit: serverTableExportLimit(sorting),
    }, [filters, grouped, noRestaurantsSelected, queryDate, sectorId, selectedResourceIds, stayStatus]);

    return (
        <>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <RightSidebarRailLayout>
                        <RightSidebarRailHeader>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: isCompactLayout
                                        ? "auto minmax(0, 1fr)"
                                        : "minmax(0, 1fr) auto minmax(0, 1fr)",
                                    alignItems: "center",
                                    gap: 1,
                                    width: "100%",
                                }}
                            >
                                    <Box sx={{justifySelf: "start", display: "flex", alignItems: "center", flexShrink: 0}}>
                                        {isCompactLayout ? (
                                            <IconButton
                                                size="small"
                                                color="inherit"
                                                aria-label={`${t("Events")}${eventCount ? ` (${eventCount})` : ""}`}
                                                aria-expanded={eventsOpen}
                                                aria-controls="restaurant-daily-events"
                                                onClick={() => setEventsOpen(open => !open)}
                                                sx={{width: 36, height: 36, borderRadius: 1}}
                                            >
                                                <Badge
                                                    color="primary"
                                                    badgeContent={eventCount}
                                                    invisible={!eventCount || eventCount <= 1}
                                                    max={99}
                                                    overlap="circular"
                                                >
                                                    <EventOutlinedIcon fontSize="small"/>
                                                </Badge>
                                            </IconButton>
                                        ) : (
                                            <Button
                                                variant="text"
                                                color="inherit"
                                                aria-expanded={eventsOpen}
                                                aria-controls="restaurant-daily-events"
                                                endIcon={(
                                                    <ExpandMoreIcon
                                                        sx={{
                                                            transition: theme => theme.transitions.create("transform"),
                                                            transform: eventsOpen ? "rotate(180deg)" : "rotate(0deg)",
                                                        }}
                                                    />
                                                )}
                                                onClick={() => setEventsOpen(open => !open)}
                                                sx={{
                                                    minWidth: 0,
                                                    px: 1,
                                                    fontWeight: 600,
                                                    textTransform: "none",
                                                }}
                                            >
                                                {t("Events")}{eventCount ? ` (${eventCount})` : ""}
                                            </Button>
                                        )}
                                        <RestaurantEventsInfoButton/>
                                    </Box>
                                    <Box sx={{justifySelf: "center", minWidth: 0, maxWidth: "100%"}}>
                                        <RightSidebarRailHeaderCenter>
                                            <SingleDatePicker
                                                value={selectedDate}
                                                onChange={onDateChange}
                                                size="medium"
                                            />
                                        </RightSidebarRailHeaderCenter>
                                    </Box>
                            </Box>
                        </RightSidebarRailHeader>

                        <Collapse in={eventsOpen} sx={{mb: eventsOpen ? 6 : 0}}>
                            <RestaurantDailyEvents
                                selectedDate={queryDate}
                                restaurants={resourceSelection.selectedResources}
                                alignGuestColumnWithExpansion={grouped}
                                onEventCountChange={setEventCount}
                                onOpenReservation={openReservationDialog}
                            />
                        </Collapse>

                        <ServerTable/>

                        <RightSidebarRail>
                            <ServerTableRailSearch/>
                            <ServerTableRailSort/>
                            <StayStatusRailFilter
                                value={stayStatus}
                                onChange={setStayStatus}
                            />
                            <RestaurantDailyListFilterButton
                                resources={resourceSelection.resources}
                                rules={filterRules}
                                onChange={setFilterRules}
                            />
                            <RightSidebarPanelButton
                                    icon={<VisibilityOutlinedIcon/>}
                                    label={t("View mode")}
                                    renderPanel={close => (
                                        <RadioFilterPanel<DailyListViewMode>
                                            title={t("View mode")}
                                            options={viewModeOptions}
                                            value={viewMode}
                                            onChange={setViewMode}
                                            onReset={grouped ? undefined : () => setViewMode("GROUPED")}
                                            onClose={close}
                                        />
                                    )}
                            />
                            <ServerTableRailFullscreen/>
                            <ServerTableRailColumns/>
                            <RightSidebarDivider/>
                            <MultipleRestaurantResourcePicker
                                restaurants={resourceSelection.resources}
                                selectedRestaurantIds={resourceSelection.selectedIds}
                                onSelectedRestaurantIdsChange={resourceSelection.setSelectedIds}
                            />
                            <ServerTableRailPrint
                                title={t("Daily List")}
                                selectedDate={queryDate}
                            />
                            <ServerTableRailExport
                                list="daily-restaurant"
                                title={t("Daily List")}
                                buildRequest={exportRequest}
                            />
                        </RightSidebarRail>
                    </RightSidebarRailLayout>
                </ServerTableFrame>
            </ServerTableProvider>

            <ProfilePreviewDialog
                open={Boolean(currentProfile)}
                onClose={() => setCurrentProfile(undefined)}
                profile={currentProfile}
                reservationId={currentReservationIds}
                extraActions={<SurveyInviteButton profileId={currentProfile?.id}/>}
                setProfile={profile => {
                    setCurrentProfile(profile);
                    void controller.query.refetch();
                }}
            />

            <ReservationDialog
                open={Boolean(selectedReservation)}
                reservation={selectedReservation}
                defaultExpandedProfile={selectedReservationProfile}
                onClose={closeReservationDialog}
                onSave={() => {
                    closeReservationDialog();
                    void controller.query.refetch();
                    void queryClient.invalidateQueries(["server-table", "restaurant-daily-events"]);
                }}
                onChangeReservation={setSelectedReservation}
            />
        </>
    );
}
